/**
 * Conversation sidebar for Assistant UI
 * Renders Featured + Recents lists in fullscreen mode.
 */
import React, { memo } from "react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { FeaturedConversationItem } from "~/components/assistant/FeaturedConversationItem";
import type { Conversation, ConversationShortcut } from "~/components/assistant/types";
import { cn } from "~/lib/utils";

interface ConversationSidebarProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  conversationShortcuts?: ConversationShortcut[];
  isStreaming?: (conversationId: string) => boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  className?: string;
}

function formatConversationLabel(conversation: Conversation): string {
  if (conversation.display_name) {
    return conversation.display_name;
  }

  const date = new Date(conversation.created_at);
  const now = new Date();

  const timeString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = (() => {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return date.toDateString() === yesterday.toDateString();
  })();

  let dateString: string;

  if (isToday) {
    dateString = "Today";
  } else if (isYesterday) {
    dateString = "Yesterday";
  } else {
    dateString = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  return `${dateString}, ${timeString}`;
}

export const ConversationSidebar = memo(function ConversationSidebar({
  conversations,
  currentConversationId,
  conversationShortcuts,
  isStreaming,
  onSelect,
  onDelete,
  onRename,
  className,
}: ConversationSidebarProps) {
  const visibleShortcuts = conversationShortcuts?.filter((s) => s.url) ?? [];

  return (
    <div
      className={cn(
        "flex flex-col h-full w-56 min-w-56 overflow-y-auto scrollbar-subtle border-r border-black/[0.12] py-2 px-2 space-y-0.5",
        className
      )}
    >
      {visibleShortcuts.length > 0 && (
        <>
          <div className="px-2 py-1 mb-2">
            <h3 className="text-xs font-medium text-gray-500 tracking-wide">Featured</h3>
          </div>
          <div className="space-y-0.5 mb-2">
            {visibleShortcuts.map((shortcut, index) => (
              <FeaturedConversationItem key={index} label={shortcut.label} url={shortcut.url} />
            ))}
          </div>
        </>
      )}
      {conversations.length > 0 && (
        <div className="px-2 py-1 mb-2">
          <h3 className="text-xs font-medium text-gray-500 tracking-wide">Recents</h3>
        </div>
      )}
      <div className="space-y-0.5">
        {conversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            conversation={conversation}
            isSelected={conversation.id === currentConversationId}
            isStreaming={isStreaming?.(conversation.id) ?? false}
            onSelect={onSelect}
            onDelete={onDelete}
            onRename={onRename}
            formatLabel={formatConversationLabel}
          />
        ))}
      </div>
    </div>
  );
});
